import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import { useState, useEffect } from 'react';
import ModalDelegate from 'store/general/modalDelegate';
import { Document } from 'types/documentsService';

interface ContextualProps {
  document: Document;
  getExistingDocumentTypes: () => string[];
  updateDocument: (document: Document) => Promise<void>;
}

interface FormFields extends DefaultFormFields {
  docName: FormControl<string>;
  description: FormControl<string>;
}

export class EditResponseDocumentModalStore {
  private readonly editDocumentModalDelegate: ModalDelegate = new ModalDelegate();
  private readonly _document: Document;
  private readonly _getExistingDocumentTypes: () => string[];
  private readonly _updateDocument: (document: Document) => Promise<void>;
  private _form: FormController<FormFields>;

  public get form(): FormController<FormFields> {
    return this._form;
  }

  public get showEditDocumentModal(): boolean {
    return this.editDocumentModalDelegate.isVisible;
  }

  public openEditDocumentModal(): void {
    // Added a reset on open but not on close due to the callback order in the edit response document modal.
    this._form.resetForm();
    this.editDocumentModalDelegate.open();
  }

  public closeEditDocumentModal(): void {
    this.editDocumentModalDelegate.close();
  }

  private _nameIsUnique(value: string): boolean {
    if (value === this._document?.type) {
      return true;
    }
    const existingDocs = this._getExistingDocumentTypes();
    return !existingDocs.find((docType) => docType.toLowerCase().trim() === value.toLowerCase().trim());
  }

  public async editResponseDocument(): Promise<void> {
    this._document.type = this._form.fields.docName.value.trim();
    this._document.description = this._form.fields.description.value.trim();

    await this._updateDocument(this._document);
    runInAction(() => {
      this._form.resetForm();
    });
  }

  constructor({ document, getExistingDocumentTypes, updateDocument }: ContextualProps) {
    this._document = document;
    this._getExistingDocumentTypes = getExistingDocumentTypes;
    this._updateDocument = updateDocument;

    this._form = new FormController<FormFields>({
      docName: new FormControl(document.type || '', {
        validations: ['printableCharacters'],
        customValidations: [
          {
            check: this._nameIsUnique.bind(this),
            msg: 'Document with the same name already exists.',
          },
        ],
        optional: false,
      }),
      description: new FormControl(document.description || '', {
        validations: ['printableCharacters'],
        optional: true,
      }),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

export function useEditResponseDocumentModalStore(props: ContextualProps): EditResponseDocumentModalStore | null {
  const [store, setStore] = useState<EditResponseDocumentModalStore | null>(
    () => new EditResponseDocumentModalStore({ ...props }),
  );

  useEffect(() => {
    autorun(() => {
      setStore(new EditResponseDocumentModalStore({ ...props }));
    });

    return (): void => {
      setStore(null);
    };
  }, [props.document]);
  return store;
}
